import React, { useState } from 'react';
import { parseAmount } from '../../utils/currency';
import { REGISTER_CHANNELS } from '../../constants/registerClosure.constants';
import { ChannelDefinition } from '../../types/registerClosure.types';
import { CashSession, closeCashSession } from '../../services/CashSessionService';
import { ClosureConfirmModal } from './components/ClosureConfirmModal';
import { ClosureHeader } from './components/ClosureHeader';
import { PaymentChannelsSection } from './components/PaymentChannelsSection';
import { ReconciliationSection } from './components/ReconciliationSection';
import './RegisterClosureView.css';

interface RegisterClosureViewProps {
  /** Sesion de caja abierta, con los totales esperados que calcula el servidor. */
  session: CashSession | null;
  onOpenSession: () => void;
  /** Se llama con el fondo para mañana cuando el servidor ya cerro la caja. */
  onCloseRegister: (leftover: number) => void;
}

export function RegisterClosureView({
  session,
  onOpenSession,
  onCloseRegister
}: RegisterClosureViewProps) {
  const [enteredAmounts, setEnteredAmounts] = useState<Record<string, string>>({
    cash: '',
    card: '',
    marcial_card: '',
    gift_card: '',
    invoice: ''
  });
  const [nextDayCash, setNextDayCash] = useState<string>('150');
  const [cashWithdrawal, setCashWithdrawal] = useState<string>('');
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [assumeDiscrepancyChecked, setAssumeDiscrepancyChecked] = useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState<boolean>(false);

  const channels: ChannelDefinition[] = REGISTER_CHANNELS;

  if (!session) {
    return (
      <div className="rcv-root">
        <div className="rcv-header">
          <div>
            <h2 className="rcv-title">Arqueo y Cierre de Caja</h2>
            <p className="rcv-subtitle">No hay una caja abierta. Ábrela para ver el arqueo y cerrar la jornada.</p>
          </div>
          <button type="button" onClick={onOpenSession} className="rcv-close-btn">
            ABRIR CAJA
          </button>
        </div>
      </div>
    );
  }

  const openingCash = session.openingCash;
  const channelOf = (id: string) => session.channels.find(c => c.channel === id);

  const userCashEntered = parseAmount(enteredAmounts.cash);

  // Ventas - devoluciones de la sesion, de todos los medios de pago.
  const totalSalesToday = session.channels.reduce((sum, c) => sum + c.net, 0);

  // El efectivo contado es el cajon entero y ya lleva el fondo de apertura
  // (lo esperado en efectivo es fondo + ventas): lo que no se deja para mañana se retira.
  const suggestedWithdrawalVal = userCashEntered - (parseAmount(nextDayCash));

  // Lo esperado lo calcula el servidor; en efectivo ya incluye el fondo.
  const getSystemTotal = (id: string) => channelOf(id)?.expected ?? 0;

  const getChannelOpsCount = (id: string) => {
    const sales = channelOf(id)?.salesCount ?? 0;
    const returns = channelOf(id)?.returnsCount ?? 0;
    return { sales, returns, totalOps: sales + returns };
  };

  const getDiscrepancy = (id: string) => {
    const entered = parseAmount(enteredAmounts[id]);
    const expected = getSystemTotal(id);
    return entered - expected;
  };

  const hasAnyDiscrepancy = channels.some(chan => {
    const diff = Math.abs(getDiscrepancy(chan.id));
    return diff >= 0.01;
  });

  const isArcheoBalanced = Math.abs(userCashEntered - ((parseAmount(nextDayCash)) + (parseAmount(cashWithdrawal)))) < 0.01;
  const hasDiscrepancy = hasAnyDiscrepancy || !isArcheoBalanced;

  const discrepanciesList = channels
    .map(chan => {
      const diff = getDiscrepancy(chan.id);
      return { id: chan.id, label: chan.label, diff };
    })
    .filter(item => Math.abs(item.diff) >= 0.01);

  const handleCloseRegisterAction = () => {
    setShowErrorMessage(null);
    setShowConfirmModal(true);
    setAssumeDiscrepancyChecked(false);
  };

  const closeModal = () => {
    setShowConfirmModal(false);
    setAssumeDiscrepancyChecked(false);
  };

  const executeClosure = async (assumed: boolean) => {
    if (isClosing) return;
    setShowConfirmModal(false);
    setShowErrorMessage(null);
    setIsClosing(true);

    const leftover = parseAmount(nextDayCash) || 150;
    const counted = channels.reduce((acc, chan) => {
      acc[chan.id] = parseAmount(enteredAmounts[chan.id]);
      return acc;
    }, {} as Record<string, number>);

    try {
      await closeCashSession(session.id, {
        counted,
        nextDayCash: leftover,
        cashWithdrawal: parseAmount(cashWithdrawal),
        assumed
      });
      onCloseRegister(leftover);
    } catch (err) {
      // La sesion sigue abierta: el cajero corrige y vuelve a intentarlo.
      setShowErrorMessage((err as any)?.message || 'No se pudo cerrar la caja.');
    } finally {
      setIsClosing(false);
    }
  };

  return (
    <div className="rcv-root">
      <ClosureHeader
        totalSalesToday={totalSalesToday}
        onCloseRegisterAction={handleCloseRegisterAction}
      />

      <div className="rcv-grid">
        <PaymentChannelsSection
          channels={channels}
          enteredAmounts={enteredAmounts}
          setEnteredAmounts={setEnteredAmounts}
          getSystemTotal={getSystemTotal}
          getDiscrepancy={getDiscrepancy}
          getChannelOpsCount={getChannelOpsCount}
        />

        <ReconciliationSection
          openingCash={openingCash}
          userCashEntered={userCashEntered}
          nextDayCash={nextDayCash}
          cashWithdrawal={cashWithdrawal}
          suggestedWithdrawalVal={suggestedWithdrawalVal}
          isArcheoBalanced={isArcheoBalanced}
          showErrorMessage={showErrorMessage}
          onNextDayCashChange={setNextDayCash}
          onCashWithdrawalChange={setCashWithdrawal}
        />
      </div>

      <ClosureConfirmModal
        isOpen={showConfirmModal}
        hasDiscrepancy={hasDiscrepancy}
        isArcheoBalanced={isArcheoBalanced}
        discrepanciesList={discrepanciesList}
        assumeDiscrepancyChecked={assumeDiscrepancyChecked}
        userCashEntered={userCashEntered}
        nextDayCash={nextDayCash}
        cashWithdrawal={cashWithdrawal}
        onAssumeChange={setAssumeDiscrepancyChecked}
        onClose={closeModal}
        onConfirm={executeClosure}
      />
    </div>
  );
}
